import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  auditsAPI, type AuditAction, type AuditActionInput, type AuditOutcome, type AuditPhoto, type QualificationQuestion,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useFormatters } from '../contexts/PreferencesContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, ClipboardCheck, Eye, ListChecks, ListPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AuditChecklist, { hasAnswer } from './audit-detail/AuditChecklist';
import AuditActionDialog from './audits/AuditActionDialog';
import AuditActionsTable from './audits/AuditActionsTable';
import { apiError } from './audits/useAuditActions';
import AuditArchivedBanner from './audit-detail/AuditArchivedBanner';
import AuditCheckControls from './audit-detail/AuditCheckControls';
import AuditCompletedBanner from './audit-detail/AuditCompletedBanner';
import AuditNotes from './audit-detail/AuditNotes';
import AuditProgressBar, { type AuditStats } from './audit-detail/AuditProgressBar';
import EditAuditDetailsDialog from './audit-detail/EditAuditDetailsDialog';
import AuditSummaryDialog, { type CheckRef, type FindingRef } from './audit-detail/AuditSummaryDialog';
import { useAuditDetail } from './audit-detail/useAuditDetail';

type ActionDialogState =
  | { mode: 'raise'; prefill: { description: string; response: number | null } }
  | { mode: 'edit'; action: AuditAction };


function scrollToElement(elementId: string) {
  document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * One audit (`/audits/:id`): a single scrolling, autosaving form. "No" flags
 * a finding, each check takes a comment, photos and actions; Review & complete
 * checks what is missing and records the outcome. Suppliers never reach it.
 */
export default function AuditDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { auditLiteEnabled, isLoading: settingsLoading } = useMenuSettings();
  const { formatDate } = useFormatters();
  const detail = useAuditDetail(Number(id));
  const { audit, template, answers } = detail;
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [actionDialog, setActionDialog] = useState<ActionDialogState | null>(null);
  const [actionsVersion, setActionsVersion] = useState<number>(0);
  /** Required checks the server reported unanswered on Complete (until the dialog is reopened). */
  const [serverMissing, setServerMissing] = useState<number[]>([]);
  const resumed = useRef<boolean>(false);

  const sections = useMemo(
    () => [...(template?.sections ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    [template],
  );
  const questions = useMemo(
    () => sections.flatMap((s) => [...(s.questions ?? [])].sort((a, b) => a.sort_order - b.sort_order)
      .map((q) => ({ question: q, section: s.title }))),
    [sections],
  );
  const photosByQuestion = useMemo(() => {
    const map: Record<number, AuditPhoto[]> = {};
    detail.photos.forEach((p) => { (map[p.question] ??= []).push(p); });
    return map;
  }, [detail.photos]);
  const actionsByResponse = useMemo(() => {
    const map: Record<number, AuditAction[]> = {};
    (audit?.actions ?? []).forEach((a) => { if (a.response) (map[a.response] ??= []).push(a); });
    return map;
  }, [audit]);

  const missing: CheckRef[] = questions
    .filter(({ question }) => question.is_required
      && (!hasAnswer(answers[question.id]) || serverMissing.includes(question.id)))
    .map(({ question, section }) => ({ questionId: question.id, text: question.question_text, section }));
  const findings: FindingRef[] = questions
    .filter(({ question }) => answers[question.id]?.flagged)
    .map(({ question, section }) => {
      const responseId = answers[question.id]?.id;
      return {
        questionId: question.id, text: question.question_text, section,
        hasAction: Boolean(responseId && actionsByResponse[responseId]?.length),
      };
    });
  // Every photo, plus each file_upload check with its file.
  const evidenceCount = detail.photos.length + questions.filter(({ question }) => question.question_type === 'file_upload'
    && Boolean(answers[question.id]?.file_value)).length;
  const stats: AuditStats = {
    answered: questions.filter(({ question }) => hasAnswer(answers[question.id])).length,
    total: questions.length,
    findings: findings.length,
    actions: audit?.actions.length ?? 0,
  };

  // Resume where the auditor left off: the first unanswered required check.
  useEffect(() => {
    if (resumed.current || !audit || !template || !detail.canEdit) return;
    resumed.current = true;
    const started = questions.some(({ question }) => hasAnswer(answers[question.id]));
    const first = missing[0];
    if (started && first) window.setTimeout(() => scrollToElement(`question-${first.questionId}`), 50);
  }, [audit, template, detail.canEdit, questions, answers, missing]);

  const raiseFrom = useCallback(async (question: QualificationQuestion) => {
    const response = await detail.responseIdFor(question.id);
    const comment = (answers[question.id]?.reviewer_notes ?? '').trim();
    setActionDialog({
      mode: 'raise',
      prefill: { description: comment ? `${question.question_text} — ${comment}` : question.question_text, response },
    });
  }, [answers, detail]);

  const saveAction = async (payload: AuditActionInput): Promise<true | string> => {
    try {
      if (actionDialog?.mode === 'edit') {
        await auditsAPI.updateAction(actionDialog.action.id, {
          description: payload.description, supplier_contact: payload.supplier_contact,
          due_date: payload.due_date, priority: payload.priority,
          // Only a changed owner is sent: one who has since left must not block other edits.
          ...(payload.owner !== actionDialog.action.owner ? { owner: payload.owner } : {}),
        });
        toast.success('Action updated');
      } else {
        await auditsAPI.raiseAction(payload);
        toast.success('Action raised');
      }
      await detail.refresh();
      setActionsVersion((v) => v + 1);
      return true;
    } catch (err: unknown) {
      return apiError(err, 'Could not save the action.');
    }
  };

  const complete = async (outcome: AuditOutcome, summary: string): Promise<string | null> => {
    const result = await detail.complete(outcome, summary);
    if (result.missing) {
      setServerMissing(result.missing);
      const n = result.missing.length;
      return `${n} required ${n === 1 ? 'check is' : 'checks are'} still not answered — see the list above.`;
    }
    if (!result.ok) return result.error ?? 'Could not complete the audit.';
    setSummaryOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const until = result.detail?.valid_until;
    toast.success(until ? `Audit completed — valid until ${formatDate(until)}` : 'Audit completed');
    return null;
  };

  const deleteAudit = async (): Promise<string | null> => {
    const failure = await detail.remove();
    if (!failure) navigate('/questionnaires?tab=audits');
    return failure;
  };

  if (!settingsLoading && !auditLiteEnabled) {
    return (
      <Card className="mx-auto max-w-lg mt-10">
        <CardContent className="py-10 text-center space-y-2">
          <ClipboardCheck className="h-10 w-10 mx-auto text-muted-foreground" />
          <h2 className="text-lg font-semibold">Audits are turned off</h2>
          <p className="text-sm text-muted-foreground">Audit Lite mode is switched off for this platform.</p>
        </CardContent>
      </Card>
    );
  }

  if (detail.loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading audit…
      </div>
    );
  }

  if ((detail.error || !audit) && user?.platform === 'SUPPLIER') {
    return (
      <Card className="mx-auto max-w-lg mt-10">
        <CardContent className="py-10 text-center space-y-3">
          <h2 className="text-lg font-semibold">Page not found</h2>
          <Button variant="outline" asChild><Link to="/dashboard">Go to dashboard</Link></Button>
        </CardContent>
      </Card>
    );
  }

  if (detail.error || !audit) {
    const notFound = detail.error === 'not_found' || detail.error === 'off';
    const copy = {
      not_found: ['Audit not found', 'This audit no longer exists, or you do not have access to it.'],
      off: ['Audits are turned off', 'Audits are turned off for this account. Ask your administrator if you need them.'],
      offline: ['No connection', 'Could not reach the server. Check your network, then try again.'],
      failed: ['Could not load the audit', 'Something went wrong on our side. Try again in a moment.'],
    }[detail.error ?? 'failed'];
    return (
      <Card className="mx-auto max-w-lg mt-10">
        <CardContent className="py-10 text-center space-y-3">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" />
          <h2 className="text-lg font-semibold">{copy[0]}</h2>
          <p className="text-sm text-muted-foreground">{copy[1]}</p>
          <div className="flex justify-center gap-2">
            {!notFound && <Button onClick={() => void detail.reload()}>Retry</Button>}
            <Button variant="outline" asChild><Link to="/questionnaires?tab=audits">Back to audits</Link></Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const completed = audit.status !== 'in_progress';

  return (
    <div className="space-y-6 pb-16">
      <AuditProgressBar audit={audit} sections={sections} stats={stats} canEdit={detail.canEdit}
        unsavedCount={detail.unsavedCount} saveError={detail.saveError}
        saveStatus={detail.saveStatus} lastSavedAt={detail.lastSavedAt} hasUnsaved={detail.hasUnsaved}
        onRetrySave={() => void detail.flush()} onJump={(sectionId) => scrollToElement(`section-${sectionId}`)}
        onReview={() => { void detail.flush(); setServerMissing([]); setSummaryOpen(true); }}
        onEditDetails={() => setDetailsOpen(true)}
        canEditDetails={detail.canEdit || (audit.status !== 'in_progress' && detail.canComplete && !audit.archived)}
        onArchive={detail.archive} onRestore={detail.unarchive} onDelete={deleteAudit} />
      <EditAuditDetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} audit={audit} onSave={detail.updateDetails} />

      {audit.archived && (
        <AuditArchivedBanner audit={audit} canRestore={detail.canArchive} onRestore={detail.unarchive} />
      )}
      {completed && (
        <AuditCompletedBanner audit={audit} canReopen={detail.canComplete} onReopen={detail.reopen} />
      )}
      {!detail.isCustomer && (
        <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <Eye className="h-4 w-4" />You are viewing this audit read-only.
        </p>
      )}

      <AuditChecklist sections={sections} answers={answers} canEdit={detail.canEdit} uploading={detail.uploading}
        onAnswer={detail.setAnswer} onFileUpload={(q, file) => void detail.uploadFile(q, file)}
        onFileDownload={(q) => void detail.downloadFile(q)} onFileRemove={(q) => void detail.removeAnswerFile(q)}
        renderFooter={(question) => {
          const answer = answers[question.id];
          return (
            <AuditCheckControls auditId={audit.id} question={question} answer={answer}
              photos={photosByQuestion[question.id] ?? []}
              actions={answer?.id ? actionsByResponse[answer.id] ?? [] : []}
              canEdit={detail.canEdit} canManageActions={detail.canFollowUp}
              uploading={Boolean(detail.uploading[question.id])}
              onComment={(text) => detail.setComment(question.id, text)}
              onFlag={(flagged) => detail.setFlag(question.id, flagged)}
              onAddPhotos={(files) => void detail.uploadPhotos(question.id, files)}
              onDeletePhoto={detail.deletePhoto}
              onDownloadPhoto={(photo) => void detail.downloadPhoto(photo)}
              onRaiseAction={() => void raiseFrom(question)}
              onOpenAction={(action) => {
                if (action.status === 'open' || action.status === 'in_progress') setActionDialog({ mode: 'edit', action });
              }} />
          );
        }} />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base"><ListChecks className="h-5 w-5" />Actions</CardTitle>
            {detail.canFollowUp && (
              <Button variant="outline" onClick={() => setActionDialog({ mode: 'raise', prefill: { description: '', response: null } })}>
                <ListPlus className="h-4 w-4 mr-1.5" />Raise action
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <AuditActionsTable key={actionsVersion} scope={{ audit: audit.id }} initialFilter="open" hideSupplier
            readOnly={!detail.canFollowUp} rows={audit.archived ? audit.actions : undefined}
            onChanged={() => void detail.refresh()} />
        </CardContent>
      </Card>

      <AuditNotes notes={audit.notes} canAdd={detail.canFollowUp} onAdd={detail.addNote} />

      <AuditSummaryDialog open={summaryOpen} onOpenChange={setSummaryOpen} missing={missing} findings={findings}
        actionsCount={stats.actions} evidenceCount={evidenceCount} validityDays={template?.validity_period_days ?? null}
        canComplete={detail.canComplete}
        onGoTo={(questionId) => { setSummaryOpen(false); window.setTimeout(() => scrollToElement(`question-${questionId}`), 150); }}
        onRaiseAction={(questionId) => {
          const question = questions.find((q) => q.question.id === questionId)?.question;
          if (question) { setSummaryOpen(false); void raiseFrom(question); }
        }}
        onComplete={complete} />

      {actionDialog && (
        <AuditActionDialog open onOpenChange={(o) => { if (!o) setActionDialog(null); }} customerId={audit.customer}
          auditId={audit.id} action={actionDialog.mode === 'edit' ? actionDialog.action : null}
          prefill={actionDialog.mode === 'raise' ? actionDialog.prefill : undefined} onSave={saveAction} />
      )}
    </div>
  );
}
